import { ChevronLeft, ChevronRight, CalendarRange } from "lucide-react";
import DateField from "@/components/DateField";
import { hoyISO, mesISOLocal } from "@/lib/api";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const pad = (n) => String(n).padStart(2, "0");

/** Rango {desde, hasta} (YYYY-MM-DD, hora Colombia) a partir del valor del selector. */
export function rangoDePeriodo(v) {
  if (!v) return {};
  if (v.modo === "rango") return { desde: v.desde || undefined, hasta: v.hasta || undefined };
  const [y, m] = (v.mes || mesISOLocal()).split("-").map(Number);
  return { desde: `${y}-${pad(m)}-01`, hasta: `${y}-${pad(m)}-${pad(new Date(y, m, 0).getDate())}` };
}

export const periodoInicial = () => ({ modo: "mes", mes: mesISOLocal(), desde: "", hasta: "" });

export function etiquetaPeriodo(v) {
  if (v.modo === "rango") return v.desde || v.hasta ? `${v.desde || "inicio"} → ${v.hasta || "hoy"}` : "Todo el historial";
  const [y, m] = (v.mes || mesISOLocal()).split("-").map(Number);
  return `${MESES[m - 1]} ${y}`;
}

/** Selector de periodo unificado: ◀ Mes ▶ · o un rango de fechas. Limpio y compacto. */
export default function PeriodoSelector({ value, onChange, className = "", testid = "periodo" }) {
  const v = value || periodoInicial();
  const [y, m] = (v.mes || mesISOLocal()).split("-").map(Number);
  const moverMes = (d) => { const x = new Date(y, m - 1 + d, 1); onChange({ ...v, modo: "mes", mes: `${x.getFullYear()}-${pad(x.getMonth() + 1)}` }); };
  const esMesActual = v.mes === mesISOLocal();
  return (
    <div className={`inline-flex flex-wrap items-center gap-1.5 ${className}`} data-testid={`${testid}-selector`}>
      <div className={`inline-flex items-center rounded-full border ${v.modo === "mes" ? "border-primary/50 bg-primary/5" : "border-border bg-card"} h-10 overflow-hidden`}>
        <button type="button" onClick={() => moverMes(-1)} className="h-10 w-9 flex items-center justify-center hover:bg-primary/10 transition-colors" title="Mes anterior" data-testid={`${testid}-mes-prev`}><ChevronLeft className="w-4 h-4" /></button>
        <label className="relative h-10 flex items-center px-1 cursor-pointer min-w-[150px] justify-center">
          <span className={`text-sm font-semibold ${v.modo === "mes" ? "text-foreground" : "text-muted-foreground"}`} data-testid={`${testid}-mes-label`}>{MESES[m - 1]} {y}</span>
          <input type="month" value={v.mes || mesISOLocal()} onChange={(e) => e.target.value && onChange({ ...v, modo: "mes", mes: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer" data-testid={`${testid}-mes-input`} />
        </label>
        <button type="button" onClick={() => moverMes(1)} className="h-10 w-9 flex items-center justify-center hover:bg-primary/10 transition-colors" title="Mes siguiente" data-testid={`${testid}-mes-next`}><ChevronRight className="w-4 h-4" /></button>
      </div>
      {!esMesActual && v.modo === "mes" && (
        <button type="button" onClick={() => onChange({ ...v, modo: "mes", mes: mesISOLocal() })} className="h-10 px-3 rounded-full text-xs text-primary hover:bg-primary/10" data-testid={`${testid}-hoy`}>Este mes</button>
      )}
      <button type="button" onClick={() => onChange({ ...v, modo: v.modo === "rango" ? "mes" : "rango", desde: v.desde || `${y}-${pad(m)}-01`, hasta: v.hasta || hoyISO() })}
        className={`h-10 px-3 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${v.modo === "rango" ? "border-primary/50 bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}
        title="Elegir un rango de fechas exacto" data-testid={`${testid}-rango-toggle`}>
        <CalendarRange className="w-3.5 h-3.5" /> Rango
      </button>
      {v.modo === "rango" && (
        <div className="inline-flex items-center gap-1.5" data-testid={`${testid}-rango-panel`}>
          <DateField value={v.desde} onChange={(d) => onChange({ ...v, desde: d })} className="w-36 h-10" data-testid={`${testid}-desde`} />
          <span className="text-xs text-muted-foreground">a</span>
          <DateField value={v.hasta} onChange={(d) => onChange({ ...v, hasta: d })} className="w-36 h-10" data-testid={`${testid}-hasta`} />
          {(v.desde || v.hasta) && <button type="button" onClick={() => onChange({ ...v, desde: "", hasta: "" })} className="h-10 px-2 text-xs text-muted-foreground hover:text-foreground" title="Sin límite de fechas (todo el historial)" data-testid={`${testid}-todo`}>Todo</button>}
        </div>
      )}
    </div>
  );
}
